import React, { MouseEvent, useState, useCallback } from 'react';
import styled from 'astroturf/react';

import { currentUser } from 'js/utils/user';
import { CheckIcon } from './Icons';

import useDelay from './useDelay';
import { Button, ButtonProps } from 'js/social/shared/design_system/Button';

const FollowingLabel = styled.span`
  @import 'stylesheets/design_system/_color.scss';

  @import 'stylesheets/design_system/font';
  @include body__small-bold;

  display: flex;
  height: 36px;
  min-width: 109px;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;

  color: $ds-primary-blue-active;
`;

type Props = ButtonProps & {
  userId: number;
  follows: boolean;
  onFollow: () => void;
  onUnfollow?: () => void;
  className?: string;
  alwaysShowFollowButton?: boolean;
};

export const FollowActionButton: React.FC<Props> = ({
  userId,
  follows,
  onFollow,
  onUnfollow,
  className,
  alwaysShowFollowButton = false,
  ...props
}) => {
  const [followed, setFollowed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDelayActive, delayBy] = useDelay();

  const follow = useCallback(
    async (event: MouseEvent) => {
      event.preventDefault();
      try {
        setLoading(true);
        delayBy(200);
        await onFollow();
        setFollowed(true);
      } finally {
        setLoading(false);
      }
    },
    [onFollow, delayBy],
  );

  const isDisabled = loading || isDelayActive;

  // show follow button only if not following (also can't follow yourself)
  // continue to show follow button if minimum load time has not expired
  if (currentUser?.id === userId) {
    return null;
  }
  if (!follows || isDelayActive) {
    return (
      <Button
        disabled={isDisabled}
        className={className}
        onClick={follow}
        {...props}
      >
        Follow
      </Button>
    );
  } else if (alwaysShowFollowButton || (follows && followed)) {
    if (onUnfollow) {
      return (
        <Button
          icon={<CheckIcon />}
          className={className}
          onClick={onUnfollow}
          {...props}
        >
          Following
        </Button>
      );
    }

    return (
      <FollowingLabel>
        <CheckIcon />
        Following
      </FollowingLabel>
    );
  }
  return null;
};
